import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import { Navigate, Link as RouterLink, useParams } from "react-router-dom";
import {
  ICycleConfigurationDetail,
  ILicenseCreditConfigurationDetail,
} from "services/KnowfullyAdminClient";
import { formatDateOnly } from "utils/dateUtils";

type DetailRow = { label: string; node: ReactNode };

// The cycle stores a month and a day without a year, so they are formatted in UTC
// against a placeholder year to avoid any time zone shift.
const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

const DetailTable = ({ rows }: { rows: DetailRow[] }) => (
  <TableContainer>
    <Table>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell component="th" scope="row" width="30%">
              <b>{row.label}</b>
            </TableCell>
            <TableCell>{row.node}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const RequirementSetDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const {
    requirementSetService,
    licensesUrl,
    licenseDetailsUrl,
  } = useGlobalContext();

  const requirementSetResult = useStrictQuery(
    ["requirementSetService.getRequirementSetById", id],
    () => requirementSetService.getRequirementSetById(id!),
    {
      enabled: !!id,
    },
  );

  const requirementSet = requirementSetResult.data?.data;
  useTitle(requirementSet?.name || "Requirement Set Details");

  if (!id) return <Navigate to={licensesUrl()} replace />;
  if (requirementSetResult.isLoading) return <Loader />;

  if (!requirementSet) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={licensesUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">{t({ id: "requirementSetNotFound" })}</Alert>
      </>
    );
  }

  const yesNoChip = (value: boolean) => (
    <Chip
      size="small"
      color={value ? "primary" : "default"}
      label={value ? t({ id: "yes" }) : t({ id: "no" })}
    />
  );

  const effectiveRange = () => {
    const from = formatDateOnly(requirementSet.effectiveDate);
    const to = formatDateOnly(requirementSet.endDate);
    if (!from) return "-";
    return to ? `${from} - ${to}` : t({ id: "fromDateOngoing" }, { date: from });
  };

  // The due date is a phrase built from the cycle date scheme, as in the design mock.
  const dueDatePhrase = (cycle: ICycleConfigurationDetail) => {
    switch (cycle.dateSchemeTypeName) {
      case "FixedDate": {
        if (!cycle.fixedMonth || !cycle.fixedDay) return null;
        const monthDay = monthDayFormatter.format(
          new Date(Date.UTC(2000, cycle.fixedMonth - 1, cycle.fixedDay)),
        );
        if (cycle.oddEvenYear === 1) {
          return t({ id: "dueDateOddYears" }, { date: monthDay });
        }
        if (cycle.oddEvenYear === 0) {
          return t({ id: "dueDateEvenYears" }, { date: monthDay });
        }
        return monthDay;
      }
      case "BirthDate":
        return t({ id: "dueDateBirthDate" });
      case "LastDayBirthMonth":
        return t({ id: "dueDateLastDayBirthMonth" });
      case "LicenseIssueDate":
        return t({ id: "dueDateLicenseIssueDate" });
      case "LastDayLicenseMonth":
        return t({ id: "dueDateLastDayLicenseMonth" });
      default:
        return cycle.dateSchemeTypeName ?? null;
    }
  };

  const detailRows: DetailRow[] = [
    { label: t({ id: "nameLabel" }), node: requirementSet.name ?? "-" },
    {
      label: t({ id: "license" }),
      node: requirementSet.licenseId ? (
        <Link
          component={RouterLink}
          to={licenseDetailsUrl(requirementSet.licenseId)}
        >
          {requirementSet.licenseDisplayName ?? requirementSet.licenseId}
        </Link>
      ) : (
        "-"
      ),
    },
    {
      label: t({ id: "statusLabel" }),
      node: requirementSet.statusName ? (
        <Chip size="small" label={requirementSet.statusName} />
      ) : (
        "-"
      ),
    },
    { label: t({ id: "effectiveLabel" }), node: effectiveRange() },
    {
      label: t({ id: "activeLabel" }),
      node: yesNoChip(!!requirementSet.isActive),
    },
    {
      label: t({ id: "carryoverAllowed" }),
      node: yesNoChip(!!requirementSet.carryoverAllowed),
    },
  ];

  const cycleRows = (cycle: ICycleConfigurationDetail): DetailRow[] => [
    {
      label: t({ id: "cycleLength" }),
      node: cycle.cycleLengthMonths
        ? t({ id: "monthsCount" }, { count: cycle.cycleLengthMonths })
        : "-",
    },
    { label: t({ id: "cycleType" }), node: cycle.cycleTypeName ?? "-" },
    { label: t({ id: "dateScheme" }), node: cycle.dateSchemeTypeName ?? "-" },
    { label: t({ id: "dueDate" }), node: dueDatePhrase(cycle) ?? "-" },
    {
      label: t({ id: "earnPeriodsPerReportPeriod" }),
      node: cycle.earnPeriodsPerReportPeriod ?? "-",
    },
    {
      label: t({ id: "gracePeriod" }),
      node: cycle.gracePeriodDays
        ? t({ id: "daysCount" }, { count: cycle.gracePeriodDays })
        : t({ id: "noneLabel" }),
    },
  ];

  const creditRows = (
    credit: ILicenseCreditConfigurationDetail,
  ): DetailRow[] => [
    {
      label: t({ id: "creditUnit" }),
      node: credit.creditUnitMinutes
        ? t({ id: "minutesCount" }, { count: credit.creditUnitMinutes })
        : "-",
    },
    {
      label: t({ id: "minimumIncrement" }),
      node: credit.minCreditIncrement
        ? t({ id: "hoursCount" }, { count: credit.minCreditIncrement })
        : "-",
    },
    {
      label: t({ id: "sameCourseRepeatAllowed" }),
      node: yesNoChip(!!credit.sameCourseCycleRepeatAllowed),
    },
    {
      label: t({ id: "documentationRetention" }),
      node: credit.documentationRetentionYears
        ? t({ id: "yearsCount" }, { count: credit.documentationRetentionYears })
        : "-",
    },
    {
      label: t({ id: "roundingDirection" }),
      node: credit.roundingDirectionTypeName ?? "-",
    },
  ];

  const accreditors = requirementSet.accreditors ?? [];

  return (
    <>
      <RedirectBackButton
        redirectRoute={licenseDetailsUrl(requirementSet.licenseId)}
        labelKey="backToLicense"
        labelValues={{ license: requirementSet.licenseDisplayName ?? "" }}
      />
      <h1>{requirementSet.name}</h1>

      <Typography variant="h5" sx={{ margin: "20px 0" }}>
        {t({ id: "detailsLabel" })}
      </Typography>
      <DetailTable rows={detailRows} />

      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "cycleConfiguration" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {requirementSet.cycleConfiguration ? (
            <DetailTable rows={cycleRows(requirementSet.cycleConfiguration)} />
          ) : (
            <Alert severity="info">{t({ id: "noCycleConfiguration" })}</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "creditConfiguration" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {requirementSet.creditConfiguration ? (
            <DetailTable
              rows={creditRows(requirementSet.creditConfiguration)}
            />
          ) : (
            <Alert severity="info">{t({ id: "noCreditConfiguration" })}</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "accreditorsLabel" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {accreditors.length > 0 ? (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {accreditors.map((accreditor, i) => (
                <Chip key={i} size="small" label={accreditor} />
              ))}
            </Box>
          ) : (
            <Alert severity="info">{t({ id: "noAccreditors" })}</Alert>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default RequirementSetDetails;
